import React, { memo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import Skeleton from '@material-ui/lab/Skeleton'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import DeleteForeverOutlinedIcon from '@material-ui/icons/DeleteForeverOutlined'
import { useDrop } from 'react-dnd'

import PlugChannel from 'components/CommonUI/svgIcons/PlugChannel'
import { hex } from 'utils/hex'
import { devicesIconComponent } from 'constants/deviceTypeConstants'
import { equal } from 'utils/memoEqual'
import {
  useCreateMappingDeviceToMeterMutation,
  useUpdateMappingDeviceToMeterMutation,
  useDeleteMappingDeviceToMeterMutation,
} from 'redux/rtk-query/devices'

import { showAlert } from 'redux/reducers/notification'

const ChannelItem = memo(
  ({
    device_id,
    channel = null,
    deviceConnected = null,
    meter_id = '',
    setMappedChanel,
    prevMappedChannel,
    isFetchingList,
    id: deviceMapingId = null,
  }) => {
    const dispatch = useDispatch()

    const [createMaping, { isLoading }] =
      useCreateMappingDeviceToMeterMutation()

    const [updateMaping, { isLoading: isUpdateLoading }] =
      useUpdateMappingDeviceToMeterMutation()

    const [deleteMaping, { isLoading: isDeleteLoading }] =
      useDeleteMappingDeviceToMeterMutation()

    const [collectedProps, drop] = useDrop(() => ({
      accept: 'DEVICE',
      drop: ({ device_id: dragedDeviceId }) => {
        if (dragedDeviceId) {
          device_id
            ? handleUpdateMapping({
                device_id: dragedDeviceId,
                deviceToChannnelMappingId: deviceMapingId,
                channel,
              })
            : handleCreateMapping({
                device_id: dragedDeviceId,
                meter_id,
                channel,
              })
        }
      },
      collect: (monitor) => {
        return {
          highlighted: monitor.canDrop(),
          hovered: monitor.isOver(),
          color: monitor.getItem()?.color,
        }
      },
    }))

    const handleDeleteMapping = useCallback(() => {
      deleteMaping(deviceMapingId)
        .unwrap()
        .then(() => {
          dispatch(
            showAlert({
              message: 'The delete mapping device to channel was successfully',
              timeoutHide: 5000,
            })
          )
          setMappedChanel(channel)
        })
        .catch(() => {
          dispatch(
            showAlert({
              message: 'The delete mapping device to channel is failed',
              severity: 'error',
              timeoutHide: 0,
            })
          )
        })
    }, [channel, deleteMaping, dispatch, deviceMapingId, setMappedChanel])

    const handleCreateMapping = useCallback(
      (data) => {
        createMaping(data)
          .unwrap()
          .then(() => {
            dispatch(
              showAlert({
                message: 'The mapping device to channel was successfully',
                timeoutHide: 5000,
              })
            )
            setMappedChanel(channel)
          })
          .catch(() => {
            dispatch(
              showAlert({
                message: 'The mapping device to channel is failed',
                severity: 'error',
                timeoutHide: 0,
              })
            )
          })
      },
      [channel, createMaping, dispatch, setMappedChanel]
    )

    const handleUpdateMapping = useCallback(
      (data) => {
        updateMaping(data)
          .unwrap()
          .then(() => {
            dispatch(
              showAlert({
                message:
                  'The update mapping device to channel was successfully',
                timeoutHide: 5000,
              })
            )
            setMappedChanel(channel)
          })
          .catch(() => {
            dispatch(
              showAlert({
                message: 'The update mapping device to channel is failed',
                severity: 'error',
                timeoutHide: 0,
              })
            )
          })
      },
      [channel, dispatch, setMappedChanel, updateMaping]
    )

    const _isLoad =
      isDeleteLoading ||
      isUpdateLoading ||
      isLoading ||
      (isFetchingList && prevMappedChannel === channel)

    const deviceDragEnterClassName =
      collectedProps?.hovered || _isLoad ? 'device_drag_enter' : ''

    const highlightedClassName = collectedProps?.highlighted
      ? 'highlighted'
      : ''

    const borderColor = collectedProps?.highlighted
      ? collectedProps?.hovered && collectedProps?.color
        ? collectedProps?.color
        : '#64686D'
      : 'rgba(21, 31, 49, 0.15)'

    return (
      <div className="channel_item_container">
        <div className="channel_item" ref={_isLoad ? null : drop}>
          <span className="number_of_channel">
            <PlugChannel className="number_of_channel_icon" />
            Сhannel {channel}
            {_isLoad && (
              <CircularProgress
                size={16}
                style={{ color: '#151F31', marginLeft: '15px' }}
              />
            )}
          </span>
          <div
            className={`channel_drop_section ${deviceDragEnterClassName} ${highlightedClassName}`}
          >
            {deviceConnected ? (
              <DeviceConnectedItem
                {...deviceConnected}
                handleDeleteMapping={handleDeleteMapping}
              />
            ) : (
              <DeviceNotConnected borderColor={borderColor} />
            )}
            {_isLoad && (
              <div className="loading_container">
                <Skeleton variant="rect" animation="wave" className="loader" />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  },
  equal
)

const DeviceConnectedItem = memo(
  ({ icon = '', color, name = '', handleDeleteMapping }) => {
    const Icon = devicesIconComponent[icon]?.ComponentIcon

    return (
      <div
        className="channel_drop_area device_block channel_to_device_mapped"
        style={{
          backgroundColor: hex(color),
        }}
      >
        <Icon className="device_item_icon" color={color} />
        <span
          className="device_item_name"
          style={{
            color,
          }}
        >
          {name}
        </span>
        <IconButton
          className="delete_device_button"
          onClick={handleDeleteMapping}
        >
          <DeleteForeverOutlinedIcon className="delete_device_icon" />
        </IconButton>
      </div>
    )
  }
)

const DeviceNotConnected = memo(({ borderColor }) => {
  return (
    <div
      className="channel_drop_area"
      style={{
        borderColor,
      }}
    >
      <span className="channel_not_connected">Not connected</span>
    </div>
  )
})

export default ChannelItem
